import { create } from "zustand";
import { DESKTOP_LAYOUT_STORAGE_KEYS } from "@/lib/localStorageKeys/desktop-layout";

export type DesktopChartLayoutMode =
  | "single"
  | "two-horizontal"
  | "two-vertical"
  | "quad";

/** Active chart panel for watchlist target + market depth (0–3). */
export type ChartFocusedPanelId = 0 | 1 | 2 | 3;

const CHART_LAYOUT_MODES: DesktopChartLayoutMode[] = [
  "single",
  "two-horizontal",
  "two-vertical",
  "quad",
];

function isDesktopChartLayoutMode(
  v: string | null,
): v is DesktopChartLayoutMode {
  return v !== null && (CHART_LAYOUT_MODES as string[]).includes(v);
}

function readChartLayoutMode(): DesktopChartLayoutMode {
  if (typeof window === "undefined") return "single";
  try {
    const raw = window.localStorage.getItem(
      DESKTOP_LAYOUT_STORAGE_KEYS.CHART_LAYOUT_MODE,
    );
    if (raw && isDesktopChartLayoutMode(raw)) return raw;
  } catch {
    /* ignore */
  }
  return "single";
}

function writeChartLayoutMode(mode: DesktopChartLayoutMode) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      DESKTOP_LAYOUT_STORAGE_KEYS.CHART_LAYOUT_MODE,
      mode,
    );
  } catch {
    /* ignore */
  }
}

function readFocusedChartPanel(): ChartFocusedPanelId {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(
      DESKTOP_LAYOUT_STORAGE_KEYS.CHART_FOCUSED_PANEL,
    );
    if (raw === null) return 0;
    const n = parseInt(raw, 10);
    if (n === 0 || n === 1 || n === 2 || n === 3) return n;
  } catch {
    /* ignore */
  }
  return 0;
}

function writeFocusedChartPanel(id: ChartFocusedPanelId) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      DESKTOP_LAYOUT_STORAGE_KEYS.CHART_FOCUSED_PANEL,
      String(id),
    );
  } catch {
    /* ignore */
  }
}

function maxPanelForLayout(mode: DesktopChartLayoutMode): ChartFocusedPanelId {
  switch (mode) {
    case "single":
      return 0;
    case "two-horizontal":
    case "two-vertical":
      return 1;
    case "quad":
      return 3;
    default:
      return 0;
  }
}

function clampFocus(
  mode: DesktopChartLayoutMode,
  id: ChartFocusedPanelId,
): ChartFocusedPanelId {
  const max = maxPanelForLayout(mode);
  return (id > max ? max : id) as ChartFocusedPanelId;
}

type DesktopLayoutStore = {
  quotesVisible: boolean;
  footerVisible: boolean;
  agentSidebarVisible: boolean;
  chartLayoutMode: DesktopChartLayoutMode;
  focusedChartPanelId: ChartFocusedPanelId;
  toggleQuotes: () => void;
  toggleFooter: () => void;
  toggleAgentSidebar: () => void;
  setQuotesVisible: (v: boolean) => void;
  setFooterVisible: (v: boolean) => void;
  setAgentSidebarVisible: (v: boolean) => void;
  setChartLayoutMode: (mode: DesktopChartLayoutMode) => void;
  setFocusedChartPanelId: (id: ChartFocusedPanelId) => void;
};

function readBool(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === "1";
  } catch {
    return fallback;
  }
}

function writeBool(key: string, v: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, v ? "1" : "0");
  } catch {
    // Quota / private-mode failures are non-fatal; ignore.
  }
}

export const useDesktopLayoutStore = create<DesktopLayoutStore>((set, get) => ({
  quotesVisible: readBool(DESKTOP_LAYOUT_STORAGE_KEYS.QUOTES_VISIBLE, true),
  footerVisible: readBool(DESKTOP_LAYOUT_STORAGE_KEYS.FOOTER_VISIBLE, true),
  agentSidebarVisible: readBool(
    DESKTOP_LAYOUT_STORAGE_KEYS.AGENT_SIDEBAR_VISIBLE,
    false,
  ),
  chartLayoutMode: readChartLayoutMode(),
  focusedChartPanelId: (() => {
    const mode = readChartLayoutMode();
    return clampFocus(mode, readFocusedChartPanel());
  })(),
  toggleQuotes: () => {
    const next = !get().quotesVisible;
    writeBool(DESKTOP_LAYOUT_STORAGE_KEYS.QUOTES_VISIBLE, next);
    set({ quotesVisible: next });
  },
  toggleFooter: () => {
    const next = !get().footerVisible;
    writeBool(DESKTOP_LAYOUT_STORAGE_KEYS.FOOTER_VISIBLE, next);
    set({ footerVisible: next });
  },
  toggleAgentSidebar: () => {
    const next = !get().agentSidebarVisible;
    writeBool(DESKTOP_LAYOUT_STORAGE_KEYS.AGENT_SIDEBAR_VISIBLE, next);
    set({ agentSidebarVisible: next });
  },
  setQuotesVisible: (v) => {
    writeBool(DESKTOP_LAYOUT_STORAGE_KEYS.QUOTES_VISIBLE, v);
    set({ quotesVisible: v });
  },
  setFooterVisible: (v) => {
    writeBool(DESKTOP_LAYOUT_STORAGE_KEYS.FOOTER_VISIBLE, v);
    set({ footerVisible: v });
  },
  setAgentSidebarVisible: (v) => {
    writeBool(DESKTOP_LAYOUT_STORAGE_KEYS.AGENT_SIDEBAR_VISIBLE, v);
    set({ agentSidebarVisible: v });
  },
  setChartLayoutMode: (mode) => {
    writeChartLayoutMode(mode);
    const prev = get().focusedChartPanelId;
    const next = clampFocus(mode, prev);
    writeFocusedChartPanel(next);
    set({ chartLayoutMode: mode, focusedChartPanelId: next });
  },
  setFocusedChartPanelId: (id) => {
    const mode = get().chartLayoutMode;
    const next = clampFocus(mode, id);
    writeFocusedChartPanel(next);
    set({ focusedChartPanelId: next });
  },
}));
